import { getLocalTodayYMD } from "@/lib/dateOnly";

/**
 * Shared state for the LR Direct guided posting wizard (TYPE mode).
 *
 * One flat state object lives in LRDirectGuidedWizardPage for the whole flow,
 * so Back never loses entered data. Each step patches it via
 * `onChange(partial)`. The Review step maps it 1:1 onto
 * `DirectLoadPostInput` (saveDirectLoadPost) — same payload keys the form uses.
 */

export interface WizardStop {
  id: string;
  stop_type: "pickup" | "delivery";
  /** Display text in the autocomplete input ("City, ST"). */
  location: string;
  name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  /** YYYY-MM-DD or "" — falls back to the step-level date at save time. */
  scheduled_date: string;
}

/** Same caps as the form's handleAddStop. */
export const MAX_PICKUPS = 5;
export const MAX_DROPS = 5;
export const MAX_TOTAL_STOPS = 10;

export const blankStop = (
  type: "pickup" | "delivery",
  pickupDate: string,
  deliveryDate: string
): WizardStop => ({
  id: crypto.randomUUID(),
  stop_type: type,
  location: "",
  name: "",
  address: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
  latitude: null,
  longitude: null,
  scheduled_date: type === "pickup" ? pickupDate : deliveryDate,
});

export interface WizardFields {
  // Route (Step 2)
  pickupCount: number;
  dropCount: number;
  stops: WizardStop[]; // pickups first, then drops — the order the save path expects
  // Dates (Step 3)
  pickupDate: string; // default today
  deliveryDate: string; // required on loads, optional on quotes
  // Equipment & weight (Step 4)
  equipmentType: string; // prefilled from profiles.default_equipment_type
  loadType: "FTL" | "LTL";
  loadNature: "spot" | "dedicated" | "contract";
  weight: string; // default "41000"
  length: string; // default "53"
  // Extras (Step 5 — all optional)
  commodity: string;
  referenceNumber: string;
  comments: string;
  shipperNotes: string;
  handlingRequirements: string[];
  // Review (Step 6) — local datetime "YYYY-MM-DDTHH:mm", auto-derived from pickup date
  bidDeadline: string;
}

export const defaultWizardFields = (): WizardFields => {
  const today = getLocalTodayYMD();
  return {
    pickupCount: 1,
    dropCount: 1,
    stops: [blankStop("pickup", today, ""), blankStop("delivery", today, "")],
    pickupDate: today,
    deliveryDate: "",
    equipmentType: "",
    loadType: "FTL",
    loadNature: "spot",
    weight: "41000",
    length: "53",
    commodity: "",
    referenceNumber: "",
    comments: "",
    shipperNotes: "",
    handlingRequirements: [],
    bidDeadline: `${today}T23:59`,
  };
};

/**
 * Regenerate the stops array for new pickup/drop counts, preserving every
 * already-resolved stop (same position, same type). New slots are blank stops
 * pre-dated with the step-level pickup/delivery dates.
 */
export function regenerateStops(
  prev: WizardStop[],
  pickupCount: number,
  dropCount: number,
  pickupDate: string,
  deliveryDate: string
): WizardStop[] {
  const prevPickups = prev.filter((s) => s.stop_type === "pickup");
  const prevDrops = prev.filter((s) => s.stop_type === "delivery");
  const pickups = Array.from(
    { length: pickupCount },
    (_, i) => prevPickups[i] ?? blankStop("pickup", pickupDate, deliveryDate)
  );
  const drops = Array.from(
    { length: dropCount },
    (_, i) => prevDrops[i] ?? blankStop("delivery", pickupDate, deliveryDate)
  );
  return [...pickups, ...drops];
}

export const pickupLabel = (index: number, total: number): string =>
  total === 1 ? "Pickup" : `Pickup ${index + 1}`;

export const dropLabel = (index: number, total: number): string =>
  total === 1 ? "Delivery" : `Drop ${index + 1}`;
